import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    getPlatformNewsReadStorageKey,
    getReadPlatformNewsIds,
    markPlatformNewsAsRead,
    PLATFORM_NEWS_READ_STATE_CHANGED,
} from "../../infrastructure/platform-news/browser-platform-news-read-store";

function haveSameNewsIds(currentIds, nextIds) {
    return (
        currentIds.length === nextIds.length &&
        currentIds.every(
            (newsId, index) => newsId === nextIds[index]
        )
    );
}

export function usePlatformNewsReadState(userId) {
    const [readNewsIds, setReadNewsIds] = useState(
        () => getReadPlatformNewsIds(userId)
    );

    useEffect(() => {
        if (typeof window === "undefined") {
            return undefined;
        }

        const storageKey = userId
            ? getPlatformNewsReadStorageKey(userId)
            : null;

        const syncReadNewsIds = (event) => {
            if (
                event &&
                event.type === "storage" &&
                event.key !== storageKey
            ) {
                return;
            }

            if (
                event &&
                event.type === PLATFORM_NEWS_READ_STATE_CHANGED &&
                event.detail &&
                event.detail.userId !== String(userId)
            ) {
                return;
            }

            const nextReadNewsIds = getReadPlatformNewsIds(userId);

            setReadNewsIds((currentReadNewsIds) =>
                haveSameNewsIds(
                    currentReadNewsIds,
                    nextReadNewsIds
                )
                    ? currentReadNewsIds
                    : nextReadNewsIds
            );
        };

        syncReadNewsIds();

        window.addEventListener(
            PLATFORM_NEWS_READ_STATE_CHANGED,
            syncReadNewsIds
        );
        window.addEventListener(
            "storage",
            syncReadNewsIds
        );

        return () => {
            window.removeEventListener(
                PLATFORM_NEWS_READ_STATE_CHANGED,
                syncReadNewsIds
            );
            window.removeEventListener(
                "storage",
                syncReadNewsIds
            );
        };
    }, [userId]);

    const readNewsIdSet = useMemo(
        () => new Set(readNewsIds),
        [readNewsIds]
    );

    const isRead = useCallback(
        (newsId) => readNewsIdSet.has(newsId),
        [readNewsIdSet]
    );

    const markAsRead = useCallback(
        (newsId) => {
            markPlatformNewsAsRead(userId, newsId);
        },
        [userId]
    );

    return {
        isRead,
        markAsRead,
    };
}
